/**
 * WellnessReportsPage — V2 main orchestrator for Wellness Reports.
 *
 * Supports 5 report types (check-in, missed-check-in, check-in-history,
 * rounds, rounds-history) with Summary/Detailed view toggle, filter drawer,
 * sort, column filters, pagination, and CSV/PDF export.
 *
 * Replaces V1 WellnessReports.jsx (~2100 lines) with clean V2 architecture.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getCampusList,
    updateSessionCampus,
    getUsername,
    getUserId,
    getSessionId,
    getCampusConfig,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, formatDateTimeIso, formatDateTime12Hour, getToday, getWeekRange, getMonthRange, getQuarterRange } from '../../../libs.jsx';
import { exportWellnessCsv, exportWellnessPdf } from '../../../services/csvExport.js';
import { fetchWellnessReport, fetchWellnessFilterOptions } from './api/wellnessApi.js';

import WellnessTable, { COLUMN_DEFS_MAP } from './components/WellnessTable.jsx';
import WellnessFilterDrawer from './components/WellnessFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';

import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import './wellness-reports-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0,0,0,0); const e = getToday(-1); e.setHours(23,59,59,999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s] = getWeekRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
];

const REPORT_TYPES = [
    { value: 'check-in', label: 'Check-in Analysis' },
    { value: 'missed-check-in', label: 'Missed Check-in' },
    { value: 'check-in-history', label: 'Check-in History' },
    { value: 'rounds', label: 'Rounds Analysis' },
    { value: 'rounds-history', label: 'Rounds History' },
];


function emptyReportFilters() {
    return {
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        deviceCategories: [],
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WellnessReportsPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = campusConfig?.tz_offset;

    // -- Navigation state (from ScheduledReportsPage "Run Report Local") --
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Report type --
    const [reportType, setReportType] = useState(navState?.subType || 'check-in');
    const isMissedCheckin = reportType === 'missed-check-in';

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range --
    const [rangeId, setRangeId] = useState(navState?.rangeId ?? 2);
    const [rangeName, setRangeName] = useState(() => {
        if (!navState?.rangeId) return 'Yesterday';
        // Match the labels used by the TIME_RANGES definitions.
        return ({ 1: 'Today', 2: 'Yesterday', 3: 'This Week', 4: 'This Month',
                  5: 'This Quarter', 6: 'Last Week', 7: 'Last Month', 8: 'Last Quarter' })[navState.rangeId] || 'Yesterday';
    });
    const [customRange, setCustomRange] = useState(false);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) return new Date(navState.startDate);
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) return new Date(navState.endDate);
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters — only accept filter keys that wellness understands.
    //    parseFilterTerms() in scheduleRunHelpers returns extra keys (users,
    //    devices, responseTime, ...) that other report pages use; copying
    //    non-array shapes into reportFilters breaks the filter drawer's
    //    assumption that every value is an array. --
    const [reportFilters, setReportFilters] = useState(() => {
        const base = emptyReportFilters();
        if (!navState?.filters) return base;
        const allowed = {};
        Object.keys(base).forEach((k) => {
            if (Array.isArray(navState.filters[k])) allowed[k] = navState.filters[k];
        });
        return { ...base, ...allowed };
    });
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- View mode --
    const [viewMode, setViewMode] = useState('summary');

    // -- Table state --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // -- Active column defs --
    const activeColumnDefs = useMemo(() => {
        return COLUMN_DEFS_MAP[reportType]?.[viewMode] || [];
    }, [reportType, viewMode]);

    const [visibleColumns, setVisibleColumns] = useState(() =>
        (COLUMN_DEFS_MAP['check-in']?.summary || []).map((c) => c.key)
    );

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Time popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    // -- UI --
    const [toast, setToast] = useState(null);
    const shouldRerunRef = useRef(false);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Update visible columns when report type or view mode changes
    // ======================================================================
    useEffect(() => {
        const cols = COLUMN_DEFS_MAP[reportType]?.[viewMode] || [];
        setVisibleColumns(cols.map((c) => c.key));
        setColumnFilters({});
        setSearch('');
        setPage(1);
        if (cols.length > 0) {
            setSortColumn(cols[0].key);
            setSortOrder('asc');
        }
    }, [reportType, viewMode]);

    // ======================================================================
    // Load filter options on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchWellnessFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
    }, [campusId]);

    // ======================================================================
    // Close time popover on outside click / Escape
    // ======================================================================
    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // ======================================================================
    // Time range helpers
    // ======================================================================
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // ======================================================================
    // Report type change
    // ======================================================================
    const handleReportTypeChange = useCallback((e) => {
        const newType = e.target.value;
        setReportType(newType);

        if (newType === 'missed-check-in') {
            setViewMode('detailed');
            // Force Today
            const todayRange = TIME_RANGES.find((r) => r.id === 1);
            if (todayRange) {
                const [s, en] = todayRange.getRange();
                setStart(s);
                setEnd(en);
                setRangeId(1);
                setRangeName('Today');
                setCustomRange(false);
            }
        } else {
            setViewMode('summary');
        }

        // Reset table state
        setSearch('');
        setColumnFilters({});
        setPage(1);

        // Re-run if report was already loaded
        if (status !== 'init') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    // ======================================================================
    // View mode change
    // ======================================================================
    const handleViewModeChange = useCallback((mode) => {
        setViewMode(mode);
        setSearch('');
        setColumnFilters({});
        setPage(1);
    }, []);

    // ======================================================================
    // Run Report
    // ======================================================================
    const handleRunReport = useCallback(async () => {
        setStatus('loading');
        setOverLimit(false);

        const now = new Date();
        const endToUse = end > now ? now : end;

        // Wellness API expects date-only format (YYYY-MM-DD), not datetime
        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        setStartDisplay(formatDateTime12Hour(start));
        setEndDisplay(formatDateTime12Hour(endToUse));

        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
        };

        const result = await fetchWellnessReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            reportType,
            filters: filterPayload,
        });

        if (result.ErrorCode) {
            const errMap = {
                11: 'Report timed out. Contact your customer success manager for large reports.',
                10: 'An unknown error occurred. Please contact customer success if the problem persists.',
                12: 'Request unauthorized. Service may be unavailable.',
            };
            showToast(errMap[result.ErrorCode] || 'Report request failed.', 'error');
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        setReportData(result.records || []);
        setSummaryData(result.summary || []);
        setPage(1);
        setStatus('ready');
    }, [start, end, campusId, reportType, reportFilters, showToast]);

    // ======================================================================
    // Re-run after filter apply / type change (if already run)
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, reportType, handleRunReport]);

    // ======================================================================
    // Auto-run on mount when arriving from ScheduledReportsPage "Run Report
    // Local". navStateRef is captured on mount so it only fires once.
    // ======================================================================
    const autoRanRef = useRef(false);
    useEffect(() => {
        if (autoRanRef.current) return;
        if (!navState?.autoRun) return;
        autoRanRef.current = true;
        handleRunReport();
    }, [navState, handleRunReport]);

    // ======================================================================
    // Auto-run after quick time-range selection from popover
    // ======================================================================
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // ======================================================================
    // Campus change
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setReportData([]);
        setSummaryData([]);
    }, []);

    // ======================================================================
    // Data pipeline: select dataset → search → column filters → sort → paginate
    // ======================================================================
    const activeData = viewMode === 'summary' ? summaryData : reportData;

    const searchedData = useMemo(() => {
        if (!search) return activeData;
        const term = search.toLowerCase();
        return activeData.filter((row) =>
            (row.resident_name || '').toLowerCase().includes(term) ||
            (row.area_name || '').toLowerCase().includes(term) ||
            (row.device_name || '').toLowerCase().includes(term) ||
            (row.manufacture_uuid || '').toLowerCase().includes(term)
        );
    }, [activeData, search]);

    const columnFilteredData = useMemo(() => {
        const active = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (active.length === 0) return searchedData;
        return searchedData.filter((row) =>
            active.every(([key, vals]) => {
                const val = row[key] != null ? String(row[key]) : '';
                return vals.includes(val);
            })
        );
    }, [searchedData, columnFilters]);

    const sortedData = useMemo(() => {
        if (!sortColumn) return columnFilteredData;
        const numericKeys = ['windows_count', 'check_in_count', 'missed_checkin_count', 'rounds_count', 'missed_rounds_count', 'count'];
        return [...columnFilteredData].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (numericKeys.includes(sortColumn)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
                return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [columnFilteredData, sortColumn, sortOrder]);

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);
    const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
    const showingEnd = Math.min(safePage * perPage, totalFiltered);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const max = 5;
        let startP = Math.max(1, safePage - Math.floor(max / 2));
        let endP = Math.min(totalPages, startP + max - 1);
        if (endP - startP < max - 1) startP = Math.max(1, endP - max + 1);
        for (let i = startP; i <= endP; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) {
                setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => {
        setColumnFilters({});
        setPage(1);
    }, []);

    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    // Filter chip generation
    const reportFilterChips = useMemo(() => {
        const chips = [];
        const labelMap = {
            areas: 'Areas', areaGroups: 'Area Groups', areaTypes: 'Area Types',
            residents: 'Residents', residentGroups: 'Resident Groups', deviceCategories: 'Device Categories',
        };
        Object.entries(reportFilters).forEach(([key, vals]) => {
            if (Array.isArray(vals) && vals.length > 0) {
                chips.push({ key, label: `${labelMap[key] || key}: ${vals.length}` });
            }
        });
        return chips;
    }, [reportFilters]);

    const activeReportFilterCount = reportFilterChips.length;

    // Schedule
    const wellnessTypeIdMap = { 'check-in': 3, 'missed-check-in': 9, 'check-in-history': 11, 'rounds': 2, 'rounds-history': 13 };
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // Export
    const exportColumnDefs = useMemo(() =>
        activeColumnDefs.filter((col) => visibleColumns.includes(col.key)),
    [activeColumnDefs, visibleColumns]);

    const handleExportCsv = useCallback(() => {
        const repLabel = REPORT_TYPES.find((r) => r.value === reportType)?.label || reportType;
        const filename = `wellness-${reportType}-${viewMode}.csv`;
        exportWellnessCsv(sortedData, exportColumnDefs, filename);
    }, [sortedData, exportColumnDefs, reportType, viewMode]);

    const handleExportPdf = useCallback(() => {
        const repLabel = REPORT_TYPES.find((r) => r.value === reportType)?.label || reportType;
        const dateRange = startDisplay && endDisplay ? `${startDisplay} – ${endDisplay}` : '';
        const filename = `wellness-${reportType}-${viewMode}.pdf`;
        exportWellnessPdf(sortedData, exportColumnDefs, `Wellness: ${repLabel} (${viewMode})`, dateRange, filename);
    }, [sortedData, exportColumnDefs, reportType, viewMode, startDisplay, endDisplay]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`ap-toast ap-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="ap-toast__close" aria-label="Dismiss">
                                {'\u2715'}
                            </button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Wellness</span>
                                </div>

                                <div className="ar-campus-row">
                                    <label htmlFor="wr-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="wr-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ar-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Wellness</h1>
                                    <select
                                        className="wr-report-type-select"
                                        value={reportType}
                                        onChange={handleReportTypeChange}
                                        title="Report type"
                                    >
                                        {REPORT_TYPES.map((rt) => (
                                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ar-title-row__actions">
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleDrawerOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeReportFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle — hidden for missed-check-in */}
                    {status === 'ready' && !overLimit && !isMissedCheckin ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button
                                    className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`}
                                    onClick={() => handleViewModeChange('summary')}
                                >
                                    Summary
                                </button>
                                <button
                                    className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`}
                                    onClick={() => handleViewModeChange('detailed')}
                                >
                                    Detailed
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">

                        {/* Report filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>
                                    Clear All
                                </button>
                            </div>
                        ) : null}

                        {/* Init state */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to generate your wellness report.</p>
                                <button
                                    className="dp-btn dp-btn--primary dp-btn--sm"
                                    onClick={handleRunReport}
                                    disabled={status === 'loading'}
                                    style={{ marginTop: 'var(--space-3)' }}
                                >
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                    {status === 'loading' ? 'Running...' : 'Run Report'}
                                </button>
                            </div>
                        ) : null}

                        {/* Loading state */}
                        {status === 'loading' ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading report data...</p>
                            </div>
                        ) : null}

                        {/* Overlimit */}
                        {status === 'ready' && overLimit ? (
                            <div className="ar-overlimit">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                                <h3>Report Too Large</h3>
                                <p>This report contains more than 5,000 records.</p>
                                {csvUrl ? (
                                    <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                        Download Full Report (CSV)
                                    </a>
                                ) : null}
                            </div>
                        ) : null}

                        {/* Report data */}
                        {status === 'ready' && !overLimit ? (
                            <div className="ar-detail-view">
                                {/* Controls row — matches AlertDetailsPage pattern */}
                                <div className="ap-controls">
                                    <div className="ap-controls__left">
                                        <div className="ap-search">
                                            <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                            </svg>
                                            <input
                                                type="search"
                                                placeholder="Search records..."
                                                value={search}
                                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                                className="ap-search__input"
                                                aria-label="Search records"
                                            />
                                        </div>
                                    </div>
                                    <div className="ap-controls__right">
                                        {sortedData.length > 0 ? (
                                            <>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                    CSV
                                                </button>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                                    <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                    PDF
                                                </button>
                                            </>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Table */}
                                {totalFiltered === 0 ? (
                                    <div className="ar-init-message" style={{ paddingTop: 'var(--space-8)' }}>
                                        <i className="fe fe-inbox" style={{ fontSize: '40px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }} />
                                        <p>No data to display for this report.</p>
                                    </div>
                                ) : (
                                    <WellnessTable
                                        data={paginatedData}
                                        allData={activeData}
                                        columnDefs={activeColumnDefs}
                                        visibleColumns={visibleColumns}
                                        sortColumn={sortColumn}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        filters={columnFilters}
                                        onFilterChange={handleColumnFilterChange}
                                        onClearFilters={handleClearColumnFilters}
                                        showFilters={viewMode === 'detailed'}
                                        tzOffset={tzOffset}
                                    />
                                )}

                                {/* Pagination — always shown when data exists (matches AlertDetailsPage) */}
                                {totalFiltered > 0 ? (
                                    <div className="ap-pagination">
                                        <p className="ap-pagination__info">
                                            Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                                            <span className="ap-pagination__emphasis">{totalFiltered}</span> records
                                        </p>
                                        <div className="ap-pagination__controls">
                                            <div className="ap-per-page">
                                                <label htmlFor="wr-per-page" className="ap-per-page__label">Rows per page:</label>
                                                <select
                                                    id="wr-per-page"
                                                    value={perPage}
                                                    onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }}
                                                    className="ap-per-page__select"
                                                >
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                    <option value={200}>200</option>
                                                    <option value={300}>300</option>
                                                </select>
                                            </div>
                                            <nav className="ap-page-nav" aria-label="Wellness pagination">
                                                <button
                                                    type="button"
                                                    onClick={() => setPage(Math.max(1, safePage - 1))}
                                                    disabled={safePage <= 1}
                                                    className="ap-page-btn"
                                                    aria-label="Previous page"
                                                >
                                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                                    </svg>
                                                </button>
                                                {pageNumbers.map((num) => (
                                                    <button
                                                        key={num}
                                                        type="button"
                                                        onClick={() => setPage(num)}
                                                        className={`ap-page-num${num === safePage ? ' is-active' : ''}`}
                                                        aria-label={`Page ${num}`}
                                                        aria-current={num === safePage ? 'page' : undefined}
                                                    >
                                                        {num}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                                                    disabled={safePage >= totalPages}
                                                    className="ap-page-btn"
                                                    aria-label="Next page"
                                                >
                                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                                    </svg>
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer */}
                    <WellnessFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                        timeRangeDisabled={isMissedCheckin}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={wellnessTypeIdMap[reportType] || 3}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default WellnessReportsPage;
